import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const AddNotesModal = ({ isOpen, onClose, onSave, initialNote = "", loading = false }) => {
  const [note, setNote] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setNote(initialNote || "");
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen, initialNote]);

  if (!isOpen) return null;

  const isSaveDisabled = note.trim() === "" || loading;
  const hasContent = note.trim() !== "";
  const borderActive = isFocused || hasContent;

  const handleSave = () => {
    if (!isSaveDisabled) onSave(note.trim());
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center font-outfit">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl py-6 px-6 relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          disabled={loading}
          className="absolute top-6 right-6 text-gray-500 hover:text-gray-800 transition-colors disabled:opacity-50"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        {/* Title */}
        <h2 className="text-[22px] font-semibold text-[#3F3F46] mb-2">Custom Notes</h2>

        <p className="text-[15px] text-[#52525B] leading-snug mb-5 pr-6">
          Add private notes for this project. These will be visible to everyone having access to this project.
        </p>

        {/* Description label */}
        <label className="block text-[15px] font-semibold text-[#3F3F46] mb-2">
          Description *
        </label>

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder="Write your notes here..."
          disabled={loading}
          rows={4}
          className={`w-full rounded-lg border px-4 py-3 text-[15px] text-[#3F3F46] placeholder:text-[#A1A1AA] resize-none outline-none transition-colors duration-150 ${
            borderActive ? "border-[#C95EBE]" : "border-gray-300 hover:border-gray-400"
          }`}
        />

        {/* Buttons */}
        <div className="flex items-center justify-center gap-4 mt-6">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-10 py-2.5 rounded-lg border border-[#C95EBE] text-[#C95EBE] font-semibold text-[15px] bg-white hover:bg-[#C95EBE]/5 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaveDisabled}
            className="px-10 py-2.5 rounded-lg text-white font-semibold text-[15px] transition-colors"
            style={{
              backgroundColor: isSaveDisabled ? "#9CA3AF" : "#C95EBE",
              cursor: isSaveDisabled ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Saving..." : "Save Note"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddNotesModal;
